import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  SvgIcon,
  Drawer,
  List,
  ListItem,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from "@mui/icons-material";
import { ReactComponent as HRIcon } from "../theme/HR-icon.svg";
import { signOut } from "firebase/auth";
import { auth } from "../services/firebase-config";
import { t } from "@lingui/macro";
import useAuth from "../hooks/useAuth";
import { useUser } from "../contexts/UserContext";

const Navbar = ({ toggleTheme, mode }) => {
  const isSmallScreen = useMediaQuery("(max-width: 600px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [cachedPhotoURL, setCachedPhotoURL] = useState(null);
  const { userFB, customData } = useUser();
  const { user } = useAuth();

  useEffect(() => {
    //console.log("Navbar: User changed:", user?.email, user?.displayName);
    if (user?.photoURL) {
      setCachedPhotoURL(user.photoURL);
    }
  }, [user]);

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        console.log(t`User logged out`);
      })
      .catch((error) => {
        console.error(t`Logout Error:`, error.message);
      });
    handleMenuClose();
  };

  const Watermark = () => (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%) rotate(-45deg)",
        fontSize: "5rem",
        color: "rgba(255, 0, 0, 0.3)",
        pointerEvents: "none",
        zIndex: 9999,
        whiteSpace: "nowrap",
      }}
    >
      THIS IS STAGING
    </div>
  );
  
  const renderThemeToggle = () => (
    <IconButton onClick={toggleTheme} color="inherit">
      {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
    </IconButton>
  );

  const renderUserAvatar = () => {
    const getAvatarFallback = () => {
      if (customData?.Username) return customData.Username.charAt(0).toUpperCase();
      if (userFB?.displayName) return userFB.displayName.charAt(0).toUpperCase();
      if (user?.email) return user.email.charAt(0).toUpperCase();
      if (user?.displayName) return user.displayName.charAt(0).toUpperCase();
      return t`U`;
    };

    return (
      <>
        {process.env.REACT_APP_BUILD_ID === "s" && <Watermark />}
        <IconButton onClick={handleAvatarClick} color="inherit">
          <Avatar>
            {cachedPhotoURL ? (
              <img
                src={cachedPhotoURL}
                alt={t`Profile`}
                style={{ width: "100%", height: "100%" }}
                onError={() => setCachedPhotoURL(null)}
              />
            ) : (
              getAvatarFallback()
            )}
          </Avatar>
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleMenuClose} component={Link} to="/profile">
            {t`My Profile`}
          </MenuItem>
          <MenuItem onClick={handleMenuClose} component={Link} to="/mystories">
            {t`View My Stories`}
          </MenuItem>
          <MenuItem onClick={handleMenuClose} component={Link} to="/mystories/new">
            {t`Write a New Story`}
          </MenuItem>
          {/* <MenuItem onClick={handleMenuClose} component={Link} to="/settings">
            {t`Settings`}
          </MenuItem> */}
          <MenuItem onClick={handleLogout}>{t`Logout`}</MenuItem>
        </Menu>
      </>
    );
  };

  const renderMenuItems = () => (
    <List>
      <ListItem>
        <Button component={Link} to="/" fullWidth sx={{ color: "secondary.contrastText" }}>
          {t`Home`}
        </Button>
      </ListItem>
    </List>
  );

  return (
    <>
      {isSmallScreen ? (
        <AppBar position="static" sx={{ backgroundColor: "primary.mainContrast" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t`menu`}
              onClick={toggleDrawer(true)}
            >
              ☰
            </IconButton>
            <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
              <Box role="presentation" onClick={toggleDrawer(false)} sx={{ backgroundColor: "primary.mainContrast" }}>
                {renderMenuItems()}
              </Box>
            </Drawer>
            <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
              {renderThemeToggle()}
              {user ? (
                renderUserAvatar()
              ) : (
                <Button color="inherit" component={Link} to="/login">
                  {t`Login`}
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      ) : (
        <AppBar position="static" sx={{ backgroundColor: "primary.mainContrast" }}>
          <Toolbar sx={{ color: "secondary.contrastText" }}>
            <Link
              to="/"
              style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <SvgIcon
                component={HRIcon}
                inheritViewBox
                sx={{
                  color: "secondary.contrastText",
                  width: 40,
                  height: 40,
                  marginRight: 2,
                }}
              />
            </Link>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button color="inherit" component={Link} to="/">
                {t`Home`}
              </Button>
              {/* <Button color="inherit" component={Link} to="/stories">
                {t`Stories`}
              </Button> */}
            </Box>
            <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
              {renderThemeToggle()}
              {user ? (
                renderUserAvatar()
              ) : (
                <Button color="inherit" component={Link} to="/login">
                  {t`Login`}
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      )}
    </>
  );
};

export default Navbar;
